import { Reveal, SectionHeading } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n";

const TEAM = [
  {
    name: "Dott. Riccardo Carugati",
    role: { it: "Amministratore Unico — Direzione Tecnica", en: "Sole Director — Technical Leadership" },
    initials: "RC",
    img: "https://customer-assets-0z36b82j.emergentagent.net/job_mediolanum-milano/artifacts/d481d9dc3fcfc6a0_Ritratto%20aziendale%20in%20ufficio.png",
    bio: {
      it: "Dottore Commercialista iscritto all'Ordine di Milano. Dirige personalmente ogni mandato dello studio: consulenza fiscale e societaria, pianificazione strategica e assistenza nelle operazioni straordinarie, con tutti i poteri per l'amministrazione ordinaria e straordinaria della società.",
      en: "Chartered Accountant registered with the Milan Order. He personally leads every engagement of the firm: tax and corporate advisory, strategic planning and assistance in extraordinary transactions, holding all powers for the ordinary and extraordinary management of the company.",
    },
    tags: { it: ["Consulenza Fiscale", "Operazioni Straordinarie", "Business Plan"], en: ["Tax Advisory", "Extraordinary Transactions", "Business Plans"] },
  },
  {
    name: "Dott.ssa Maria Dolgopolenco",
    role: { it: "Socia Fondatrice", en: "Founding Partner" },
    initials: "MD",
    bio: {
      it: "Socia fondatrice di Mediolanum Consulting Srl. Supervisiona la strategia della società e la gestione dei rapporti istituzionali con la clientela, garantendo la continuità dell'indirizzo imprenditoriale dello studio.",
      en: "Founding partner of Mediolanum Consulting Srl. She oversees the company's strategy and the management of institutional relationships with clients, ensuring the continuity of the firm's entrepreneurial direction.",
    },
    tags: { it: ["Strategia", "Relazioni Istituzionali", "Governance"], en: ["Strategy", "Institutional Relations", "Governance"] },
  },
];

const PARTNERS = [
  {
    name: "Marco Crippa",
    role: { it: "Partner — Responsabile Contabilità e Bilancio", en: "Partner — Head of Accounting & Financial Statements" },
    initials: "MC",
    bio: {
      it: "Responsabile del settore contabilità e bilancio. Coordina la tenuta della contabilità ordinaria, la redazione dei bilanci d'esercizio e l'analisi dei dati economico-finanziari a supporto delle attività di consulenza dello studio.",
      en: "Head of the accounting and financial statements department. He coordinates day-to-day bookkeeping, the preparation of annual financial statements and the analysis of financial data supporting the firm's advisory work.",
    },
    tags: { it: ["Contabilità", "Bilancio d'Esercizio", "Analisi Finanziaria"], en: ["Accounting", "Financial Statements", "Financial Analysis"] },
  },
  {
    name: "Dott. Sergio Calcagnile",
    role: { it: "Partner — Consulente OAM, Mediazione Creditizia", en: "Partner — OAM-registered Consultant, Credit Brokerage" },
    initials: "SC",
    img: "/team/calcagnile.png",
    bio: {
      it: "Consulente iscritto all'OAM, specializzato nella mediazione creditizia e nel supporto finanziario alle imprese. Offre consulenza e affiancamento per l'ottimizzazione dell'accesso al credito, strutturando soluzioni di finanziamento aziendale, finanza straordinaria, liquidità e operazioni di leasing personalizzate in base al profilo di crescita del cliente.",
      en: "OAM-registered consultant specialising in credit brokerage and financial support for businesses. He provides advisory and guidance to optimise access to credit, structuring corporate financing solutions, extraordinary finance, liquidity and leasing operations tailored to the client's growth profile.",
    },
    tags: { it: ["Mediazione Creditizia", "Finanza Straordinaria", "Leasing"], en: ["Credit Brokerage", "Extraordinary Finance", "Leasing"] },
  },
  {
    name: "Dott. Gustavo Astorino",
    role: { it: "Partner — Pratiche Notarili, Immobiliare e Societario", en: "Partner — Notarial Practices, Real Estate and Corporate" },
    initials: "GA",
    img: "/team/astorino.png",
    bio: {
      it: "Assistenza e consulenza tecnica per la gestione e il perfezionamento delle pratiche notarili, sia in ambito immobiliare sia societario. Collabora a stretto contatto con professionisti e imprese, curando con rigorosa precisione l'analisi documentale, il reperimento e la predisposizione degli atti funzionali a rogiti, compravendite e operazioni straordinarie.",
      en: "Technical assistance and advisory for the management and completion of notarial practices, both in real estate and corporate matters. He works closely with professionals and businesses, ensuring rigorous precision in document analysis, retrieval and preparation of deeds for transfers, purchases and extraordinary transactions.",
    },
    tags: { it: ["Atti Notarili", "Rogiti e Compravendite", "Operazioni Immobiliari"], en: ["Notarial Deeds", "Transfers & Purchases", "Real Estate Transactions"] },
  },
];

const TEXT = {
  it: {
    seoTitle: "Team e Partner dello Studio | Mediolanum Consulting Milano",
    seoDesc: "I soci e i partner di Mediolanum Consulting: Dott. Riccardo Carugati, Dott.ssa Maria Dolgopolenco, Dott. Sergio Calcagnile, Dott. Gustavo Astorino.",
    eyebrow: "I Soci",
    copy: "In Mediolanum Consulting non esistono gerarchie nascoste: chi firma la consulenza è chi incontrate al tavolo.",
    partnersEyebrow: "I Partner",
    networkEyebrow: "Network di Professionisti",
    networkCopy: "Per gli incarichi che richiedono competenze specialistiche ulteriori — notarili, giuslavoristiche, valutative o di corporate finance — lo studio attiva una rete selezionata di professionisti esterni con cui collabora stabilmente, mantenendo sempre la direzione unitaria del mandato.",
  },
  en: {
    seoTitle: "Team and Partners | Mediolanum Consulting Milan",
    seoDesc: "The partners of Mediolanum Consulting: Dr. Riccardo Carugati, Dr. Maria Dolgopolenco, Dr. Sergio Calcagnile, Dr. Gustavo Astorino.",
    eyebrow: "The Partners",
    copy: "At Mediolanum Consulting there are no hidden hierarchies: the person who signs the advice is the person you meet at the table.",
    partnersEyebrow: "External Partners",
    networkEyebrow: "Professional Network",
    networkCopy: "For engagements requiring further specialist expertise — notarial, employment law, valuation or corporate finance — the firm activates a selected network of external professionals with whom it collaborates on an ongoing basis, always maintaining unified direction of the mandate.",
  },
};

function PersonCard({ m, testid, lang }) {
  return (
    <div className="card-surface card-surface-hover h-full rounded-2xl p-8 sm:p-10" data-testid={testid}>
      <div className="flex items-center gap-6">
        {m.img ? (
          <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-gold/40 shadow-lg shadow-black/40">
            <img src={m.img} alt={m.name} className="h-full w-full object-cover object-top" />
          </div>
        ) : (
          <div className="relative flex h-24 w-24 shrink-0 items-center justify-center">
            <span className="absolute inset-0 rotate-45 rounded-xl border border-gold/40 bg-navy-900" />
            <span className="relative font-serif text-3xl font-light text-gold-light">{m.initials}</span>
          </div>
        )}
        <div>
          <h3 className="font-serif text-2xl text-slate-100">{m.name}</h3>
          <p className="mt-2 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-gold">{m.role[lang]}</p>
        </div>
      </div>
      <p className="mt-7 text-sm font-light leading-relaxed text-slate-400 sm:text-base">{m.bio[lang]}</p>
      <div className="mt-7 flex flex-wrap gap-2 border-t border-white/10 pt-6">
        {m.tags[lang].map((tag) => (
          <span key={tag} className="rounded-full border border-gold/25 px-3.5 py-1.5 text-xs text-gold-light">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Team() {
  const { lang } = useLang();
  const c = TEXT[lang];

  return (
    <main data-testid="team-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section className="navy-mesh mx-auto max-w-7xl px-5 pb-16 pt-44 sm:px-8">
        <SectionHeading
          level={1}
          eyebrow={c.eyebrow}
          title={lang === "en"
            ? <>Few, identifiable, <span className="text-gold-gradient italic">directly accountable.</span></>
            : <>Pochi, identificabili, <span className="text-gold-gradient italic">direttamente responsabili.</span></>}
          copy={c.copy}
        />
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="team-grid">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <PersonCard m={m} testid={`team-card-${i}`} lang={lang} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <p className="eyebrow" data-testid="partners-eyebrow">{c.partnersEyebrow}</p>
          <h2 className="mt-4 font-serif text-2xl font-normal tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            {lang === "en"
              ? <>Specialist expertise, <span className="text-gold-gradient italic">alongside the firm.</span></>
              : <>Competenze specialistiche, <span className="text-gold-gradient italic">a fianco dello studio.</span></>}
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="partners-grid">
          {PARTNERS.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <PersonCard m={m} testid={`partner-card-${i}`} lang={lang} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="card-surface flex flex-col items-start gap-6 rounded-2xl p-8 sm:flex-row sm:items-center sm:p-10" data-testid="team-network">
            <LogoMark size={56} />
            <div>
              <p className="eyebrow">{c.networkEyebrow}</p>
              <p className="mt-4 max-w-3xl text-sm font-light leading-relaxed text-slate-400">{c.networkCopy}</p>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
